import React, { ReactNode } from 'react';
import styled from 'styled-components/macro';
import { rgba } from 'polished';
import { useLocation } from 'react-router-dom';

import {
  Chip,
  Collapse,
  ListItemButton,
  Button,
  ListItemText,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Icon } from 'react-feather';
import { NavLinkWithNoStyle } from '../NavLinkWithNoStyle';

const Item = styled(ListItemButton)`
  outline: none;
  padding-top: ${(props) => props.theme.spacing(3)};
  padding-bottom: ${(props) => props.theme.spacing(3)};
  padding-left: ${(props) => props.theme.spacing(8)};
  padding-right: ${(props) => props.theme.spacing(7)};
  font-weight: ${(props) => props.theme.typography.fontWeightRegular};
  svg {
    color: ${(props) => props.theme.sidebar.color};
    font-size: 20px;
    width: 20px;
    height: 20px;
    opacity: 0.5;
  }
  &:hover {
    background: rgb(20 61 101) !important;
    color: ${(props) => props.theme.sidebar.color};
  }
  &:focus {
    background: #1976d2;
    color: ${(props) => props.theme.sidebar.color};
  }
  &:active {
    background: #1976d2 !important;
    color: ${(props) => props.theme.sidebar.color};
  }
` as typeof Button;

const Title = styled(ListItemText)`
  margin: 0;
  span {
    color: ${(props) => rgba(props.theme.sidebar.color, 1)};
    font-size: ${(props) => props.theme.typography.body1.fontSize}px;
    padding: 0 ${(props) => props.theme.spacing(4)};
  }
`;

const Badge = styled(Chip)`
  font-weight: ${(props) => props.theme.typography.fontWeightBold};
  height: 20px;
  position: absolute;
  right: 26px;
  top: 12px;
  background: ${(props) => props.theme.sidebar.badge.background};
  z-index: 1;
  span.MuiChip-label,
  span.MuiChip-label:hover {
    font-size: 11px;
    cursor: pointer;
    color: ${(props) => props.theme.sidebar.badge.color};
    padding-left: ${(props) => props.theme.spacing(2)};
    padding-right: ${(props) => props.theme.spacing(2)};
  }
`;

const ExpandLessIcon = styled(ExpandLess)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const ExpandMoreIcon = styled(ExpandMore)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const SidebarNavListItem = (props: {
  title: string;
  href: string;
  children?: ReactNode;
  icon: Icon;
  badge: string;
  open?: boolean;
}) => {
  const {
    title,
    href,
    children,
    icon: Icon,
    badge,
    open: openProp = false,
  } = props;

  const [open, setOpen] = React.useState(openProp);
  const location = useLocation();

  const handleToggle = () => {
    setOpen((state) => !state);
  };

  const isActive = location.pathname === href;

  if (children) {
    return (
      <React.Fragment>
        <Item className={isActive ? 'active' : ''} onClick={handleToggle}>
          {Icon && <Icon />}
          <Title>
            {title}
            {badge && <Badge label={badge} />}
          </Title>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Item>
        <Collapse in={open}>{children}</Collapse>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <NavLinkWithNoStyle to={href}>
        <Item style={{ backgroundColor: isActive ? '#1976d2' : '' }}>
          {Icon && <Icon />}
          <Title>
            {title}
            {badge && <Badge label={badge} />}
          </Title>
        </Item>
      </NavLinkWithNoStyle>
    </React.Fragment>
  );
};

export default SidebarNavListItem;
